import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import LinearProgress from '@mui/material/LinearProgress';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';
import { isWebpSupported } from 'react-image-webp/dist/utils';
import parseHtmlToReact from '../../../helpers/parseHtmlToReact';
import shadow from '../../../helpers/shadow';
import { getUser } from '../../../helpers/token';
import Link from '@mui/material/Link';
import RoundNavIconBtn from '../../Header/RoundNavIconBtn';
import ProfileAvatar from '../../Profile/ProfileAvatar';
import ProfileName from '../../Profile/ProfileName';
import CreationTime from '../CreationTime';
import DotMenu from '../DotMenu';
import CommentDeleteDialog from './CommentDeleteDialog';
import CommentHeader from './CommentHeader';
import MarkCommentAsReadButton from './MarkCommentAsReadButton';
import ReplyIcon from '@mui/icons-material/Reply';
import Collapse from '@mui/material/Collapse';
import LoginButton from '../../Header/LoginButton';
import ExternalLinkModal from 'components/Post/ExternalLinkModal';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { useMutation } from '@apollo/client';
import { UNGLAG_COMMENT } from 'helpers/graphql/hosting';
import { red } from '@mui/material/colors';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const redTheme = createTheme({
  palette: {
    primary: red,
  },
});

const CommentEditor = dynamic(() => import('../../Editor/CommentEditor'), {
  loading: () => <LinearProgress />,
  ssr: false,
});
const EditComment = dynamic(() => import('./EditComment'), {
  loading: () => <LinearProgress />,
  ssr: false,
});

const useStyles = makeStyles()(() => ({
  card: { borderRadius: 12, boxShadow: shadow.md },
  typography: {
    lineHeight: 1,
  },
}));

const CommentItem = props => {
  const { classes } = useStyles();
  const showBigAvatars = useMediaQuery('(min-width: 768px)');

  const {
    comment,
    comments,
    ignoreDepth,
    showReadButton,
    hideReplyBtn,
    withBtmPadding,
    withMarginTop,
    increaseUnreadCountOffset,
  } = props;
  const {
    createdAt,
    author,
    permlink,
    depth,
    _id,
    rootTitle,
    rootAuthor,
    rootPermlink,
    parentAuthor,
    parentPermlink,
    rootId,
    isFlagged,
  } = comment;
  const { username, avatar } = author;

  const [webpSupport, setWebpSupport] = useState(undefined);
  const [isEdit, setIsEdit] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showReplyEditor, setShowReplyEditor] = useState(false);
  const [spoofComment, setSpoofComment] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [body, setBody] = useState(comment.body);
  const [showSignUpPopup, setShowSignUpPopup] = useState(false);
  const [externalLinkModal, setExternalLinkModal] = useState(undefined);
  const [showFlagged, setShowFlagged] = useState(true);
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const loggedInUser = getUser();
    setUser(loggedInUser);
    if (loggedInUser === props.comment.author.username) {
      setIsEdit(true);
    } else setIsEdit(false);

    setWebpSupport(isWebpSupported());
  }, [props]);

  const onCommentAdd = userComment => {
    // eslint-disable-next-line no-param-reassign
    userComment.depth = depth + 1;
    setSpoofComment(userComment);
  };

  const onCommentEdit = editedComment => {
    if (editedComment && editedComment.body) {
      setBody(editedComment.body);
      setShowEditor(false);
    }
  };

  const reactParsed = parseHtmlToReact(body, {
    webpSupport,
    setExternalLinkModal,
  });
  const { bodyText } = reactParsed;

  const subComments = [];
  if (comments)
    comments.forEach(c => {
      if (c.parentId === _id) {
        subComments.push(c);
      }
    });

  function handleReplyClick() {
    const user = getUser();
    if (user) setShowReplyEditor(true);
    else setShowSignUpPopup(true);
  }

  const [approveComment] = useMutation(UNGLAG_COMMENT, {
    onCompleted: data => {
      if (data.unFlagComment.success) {
        // Handle successful approval
        console.log('Comment approved successfully');
        setShowFlagged(false);
      }
    },
    onError: error => {
      console.error('Error approving comment:', error);
    },
  });

  const handleApproveComment = () => {
    approveComment({ variables: { commentId: _id } });
  };

  const handleDeleteComment = () => {
    setDeleteOpen(true);
  };

  return (
    <>
      <Collapse in={!deleted}>
        <>
          <div
            style={
              ignoreDepth || depth === 1
                ? undefined
                : {
                    paddingLeft: `${
                      showBigAvatars ? depth * 60 - 60 : depth * 20 - 10
                    }px`,
                  }
            }
            className={'container-fluid pt-2' + (withMarginTop ? ' mt-3' : '')}
          >
            <div className="row justify-content-center">
              <div className="col-12 p-0">
                <div className="d-xl-block d-lg-block d-md-block d-sm-none d-none h-100 float-left pr-2">
                  <Link href={`/@${username}`}>
                    <RoundNavIconBtn style={{ padding: 0 }}>
                      <ProfileAvatar
                        size={50}
                        author={username}
                        authorAvatar={avatar}
                      />
                    </RoundNavIconBtn>
                  </Link>
                </div>
                <Card className={classes.card}>
                  <div className="tw-flex">
                    <div className="tw-grow">
                      <div className="container-fluid pt-1 pb-1">
                        <div className="row">
                          <div
                            className={
                              showReadButton
                                ? 'col-xl-8 col-lg-7 col-md-6 col-sm-12 col-12'
                                : 'col-xl-10 col-lg-9 col-md-8 col-sm-12 col-12'
                            }
                          >
                            <div
                              className="d-xl-none d-lg-none d-md-none d-sm-block d-block float-left pr-1"
                              style={{ paddingTop: '0.28rem' }}
                            >
                              <ProfileAvatar
                                size={20}
                                author={username}
                                authorAvatar={avatar}
                              />
                            </div>
                            <div className="pt-1 pr-2">
                              <ProfileName author={author} /> <span> · </span>{' '}
                              <Typography
                                color="textSecondary"
                                variant="overline"
                                display="inline"
                                className={classes.typography}
                              >
                                <CreationTime createdAt={createdAt} />
                              </Typography>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="tw-shrink-0 tw-grow-0">
                      <div>
                        {showReadButton && (
                          <MarkCommentAsReadButton
                            comment={comment}
                            onMarkedAsRead={() => {
                              setDeleted(true);
                              if (increaseUnreadCountOffset)
                                increaseUnreadCountOffset();
                            }}
                          />
                        )}
                        {!hideReplyBtn && (
                          <Button onClick={handleReplyClick} aria-label="reply">
                            <ReplyIcon fontSize="small" />
                            <span className="d-none d-xl-block ml-1">
                              Reply
                            </span>
                          </Button>
                        )}
                        <DotMenu
                          alwaysShowSaveBtn
                          onEditClick={
                            isEdit ? () => setShowEditor(true) : undefined
                          }
                          author={username}
                          permlink={permlink}
                          title={`Re: ${rootTitle}`}
                          isCommentMode
                          replyAction={
                            hideReplyBtn ? handleReplyClick : undefined
                          }
                          onCommentDelete={
                            rootAuthor === user || isEdit
                              ? () => setDeleteOpen(true)
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="pl-3 pr-3">
                    {isFlagged && showFlagged && (
                      <Alert severity="warning" className="mb-2">
                        <AlertTitle>Flagged as spam</AlertTitle>
                        This comment has been flagged by our spam detection
                        system. It is not visible publicly until you approve it.
                        <div className="mt-2">
                          <Button
                            color="primary"
                            size="small"
                            onClick={handleApproveComment}
                            className="mr-2"
                            variant="outlined"
                          >
                            Approve Comment
                          </Button>
                          <ThemeProvider theme={redTheme}>
                            <Button
                              color="primary"
                              size="small"
                              onClick={handleDeleteComment}
                              variant="outlined"
                            >
                              Delete Comment
                            </Button>
                          </ThemeProvider>
                        </div>
                      </Alert>
                    )}
                    {parentAuthor && (
                      <CommentHeader
                        comment={{
                          depth,
                          rootTitle,
                          rootAuthor,
                          rootPermlink,
                          parentAuthor,
                          parentPermlink,
                        }}
                      />
                    )}
                    {(showEditor && (
                      <EditComment
                        body={body}
                        editMode={isEdit}
                        permlink={permlink}
                        onCommentEdit={onCommentEdit}
                        onClose={() => setShowEditor(false)}
                        onCommentAdd={onCommentAdd}
                      />
                    )) || (
                      <div
                        className={`textPrimary postcontent${
                          withBtmPadding ? ' pb-2' : ''
                        }`}
                      >
                        <Typography variant="body1">{bodyText}</Typography>
                      </div>
                    )}
                  </div>
                  {showReplyEditor && (
                    <CommentEditor
                      parent_author={username}
                      parent_permlink={permlink}
                      onClose={() => setShowReplyEditor(false)}
                      onCommentAdd={onCommentAdd}
                      parentId={_id}
                      rootId={rootId}
                      rootAuthor={rootAuthor}
                      rootPermlink={rootPermlink}
                      rootTitle={rootTitle}
                      depth={depth + 1}
                    />
                  )}
                </Card>
              </div>
            </div>
          </div>
          {spoofComment && <CommentItem comment={spoofComment} />}
          {subComments.length > 0 ? (
            subComments.map(subComment => {
              return (
                <CommentItem
                  key={`${subComment.author.username}/${subComment.permlink}`}
                  comment={subComment}
                  comments={comments}
                />
              );
            })
          ) : (
            <></>
          )}
          <CommentDeleteDialog
            open={deleteOpen}
            setOpen={setDeleteOpen}
            setDeleted={setDeleted}
            permlink={permlink}
            author={rootAuthor === user ? username : undefined}
            btnText="Delete Comment Forever"
            dialogText="If you delete this comment, it will be gone forever and cannot be restored."
          />
        </>
      </Collapse>
      {showSignUpPopup && (
        <LoginButton
          open={showSignUpPopup}
          hideButtons
          onClickClose={() => setShowSignUpPopup(false)}
          text=" and reply to comments"
        />
      )}
      <ExternalLinkModal
        externalLinkModal={externalLinkModal}
        setExternalLinkModal={setExternalLinkModal}
      />
    </>
  );
};

export default CommentItem;
